import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, DownloadIcon, FilterIcon, FileTextIcon, ShieldCheckIcon, LockIcon, ChevronDownIcon, ClockIcon, UserIcon, ActivityIcon } from 'lucide-react';
import { MOCK_AUDIT_LOGS } from '../../constants/data';
import { formatDate } from '../../utils/dateUtils';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Table } from '../../components/ui/Table';
// Stats card for audit summary
function AuditStat({
  icon: Icon,
  label,
  value,
  color
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="p-4 bg-white rounded-xl border border-surface-200" data-id="element-1442">
      <div className="flex items-center gap-3" data-id="element-1443">
        <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center`} data-id="element-1444">
          <Icon className="w-5 h-5" data-id="element-1445" />
        </div>
        <div data-id="element-1446">
          <p className="text-heading-md font-bold text-surface-900 tabular-nums" data-id="element-1447">
            {value}
          </p>
          <p className="text-body-sm text-surface-500" data-id="element-1448">{label}</p>
        </div>
      </div>
    </motion.div>;
}
export function AuditTrail() {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const columns = [{
    key: 'timestamp',
    header: 'Timestamp',
    sortable: true,
    render: (value: unknown) => <div className="flex items-center gap-2" data-id="element-1449">
          <ClockIcon className="w-4 h-4 text-surface-400" data-id="element-1450" />
          <span className="tabular-nums text-body-sm font-medium text-surface-900" data-id="element-1451">
            {formatDate(value as string, {
          includeTime: true
        })}
          </span>
        </div>
  }, {
    key: 'userName',
    header: 'User',
    render: (value: unknown) => <div className="flex items-center gap-2" data-id="element-1452">
          <div className="w-7 h-7 bg-surface-100 rounded-lg flex items-center justify-center" data-id="element-1453">
            <UserIcon className="w-4 h-4 text-surface-500" data-id="element-1454" />
          </div>
          <span className="font-medium text-surface-900" data-id="element-1455">
            {value as string}
          </span>
        </div>
  }, {
    key: 'action',
    header: 'Action',
    render: (value: unknown) => <span className="px-2.5 py-1 bg-surface-100 text-surface-700 text-caption font-semibold rounded-lg" data-id="element-1456">
          {value as string}
        </span>
  }, {
    key: 'resource',
    header: 'Resource',
    render: (value: unknown) => <span className="text-body-sm text-surface-600" data-id="element-1457">{value as string}</span>
  }, {
    key: 'details',
    header: 'Details',
    render: (value: unknown) => <span className="text-body-sm text-surface-600 max-w-xs truncate block" data-id="element-1458">
          {value as string}
        </span>
  }, {
    key: 'ipAddress',
    header: 'IP Address',
    render: (value: unknown) => <span className="tabular-nums text-body-sm text-surface-500 font-mono" data-id="element-1459">
          {value as string}
        </span>
  }];
  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsExporting(false);
  };
  const filteredLogs = MOCK_AUDIT_LOGS.filter(log => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return log.userName.toLowerCase().includes(query) || log.action.toLowerCase().includes(query) || log.resource.toLowerCase().includes(query) || log.details.toLowerCase().includes(query);
  });
  return <div className="space-y-6" data-id="element-1460">
      {/* Header Banner */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-1461">
        <Card variant="flat" padding="lg" className="bg-gradient-to-r from-surface-800 via-surface-900 to-surface-800 text-white border-0 overflow-hidden relative" data-id="element-1462">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5" data-id="element-1463">
            <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '24px 24px'
          }} data-id="element-1464" />
          </div>

          <div className="flex items-center gap-4 relative" data-id="element-1465">
            <motion.div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm" animate={{
            rotate: [0, 5, -5, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity
          }} data-id="element-1466">
              <ShieldCheckIcon className="w-7 h-7" data-id="element-1467" />
            </motion.div>
            <div data-id="element-1468">
              <div className="flex items-center gap-3 mb-1" data-id="element-1469">
                <h1 className="text-heading-lg font-bold" data-id="element-1470">
                  Immutable Audit Trail
                </h1>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 rounded-lg text-caption font-medium" data-id="element-1471">
                  <LockIcon className="w-3.5 h-3.5" data-id="element-1472" />
                  Read-only
                </span>
              </div>
              <p className="text-body-md text-surface-300" data-id="element-1473">
                Complete transaction history. Data cannot be modified or
                deleted.
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1474">
        <AuditStat icon={FileTextIcon} label="Total Records" value={MOCK_AUDIT_LOGS.length.toString()} color="bg-primary-100 text-primary-600" data-id="element-1475" />
        <AuditStat icon={UserIcon} label="Unique Users" value="12" color="bg-success-100 text-success-600" data-id="element-1476" />
        <AuditStat icon={ActivityIcon} label="Actions Today" value="156" color="bg-warning-100 text-warning-600" data-id="element-1477" />
        <AuditStat icon={ShieldCheckIcon} label="Compliance Score" value="100%" color="bg-surface-100 text-surface-600" data-id="element-1478" />
      </div>

      {/* Search & Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} data-id="element-1479">
        <Card variant="elevated" padding="md" className="shadow-lg border-0" data-id="element-1480">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-1481">
            <div className="flex-1" data-id="element-1482">
              <Input placeholder="Search by user, action, or resource..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} leftIcon={<SearchIcon className="w-4 h-4" data-id="element-1484" />} data-id="element-1483" />
            </div>
            <div className="flex gap-2" data-id="element-1485">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1486">
                <Button variant="secondary" icon={<FilterIcon className="w-4 h-4" data-id="element-1488" />} onClick={() => setShowFilters(!showFilters)} className={showFilters ? 'bg-primary-50 text-primary-600 border-primary-200' : ''} data-id="element-1487">
                  Filters
                  <ChevronDownIcon className={`w-4 h-4 ml-1 transition-transform ${showFilters ? 'rotate-180' : ''}`} data-id="element-1489" />
                </Button>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1490">
                <Button variant="secondary" icon={<DownloadIcon className="w-4 h-4" data-id="element-1492" />} onClick={handleExport} loading={isExporting} data-id="element-1491">
                  Export CSV
                </Button>
              </motion.div>
            </div>
          </div>

          <AnimatePresence data-id="element-1493">
            {showFilters && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="overflow-hidden" data-id="element-1494">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-surface-100" data-id="element-1495">
                  <Select label="Action Type" placeholder="All actions" options={[{
                value: '',
                label: 'All Actions'
              }, {
                value: 'REFUND_INITIATED',
                label: 'Refund Initiated'
              }, {
                value: 'KYC_VERIFIED',
                label: 'KYC Verified'
              }, {
                value: 'REFUND_COMPLETED',
                label: 'Refund Completed'
              }]} value="" onChange={() => {}} data-id="element-1496" />
                  <Select label="Resource" placeholder="All resources" options={[{
                value: '',
                label: 'All Resources'
              }, {
                value: 'refund',
                label: 'Refund'
              }, {
                value: 'user',
                label: 'User'
              }, {
                value: 'invoice',
                label: 'Invoice'
              }]} value="" onChange={() => {}} data-id="element-1497" />
                  <Input label="From Date" type="date" data-id="element-1498" />
                  <Input label="To Date" type="date" data-id="element-1499" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </Card>
      </motion.div>

      {/* Audit Log Table */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} data-id="element-1500">
        <Card variant="elevated" padding="none" className="shadow-lg border-0 overflow-hidden" data-id="element-1501">
          <div className="p-6 border-b border-surface-100 flex items-center justify-between" data-id="element-1502">
            <div className="flex items-center gap-3" data-id="element-1503">
              <div className="w-10 h-10 bg-surface-100 rounded-xl flex items-center justify-center" data-id="element-1504">
                <FileTextIcon className="w-5 h-5 text-surface-600" data-id="element-1505" />
              </div>
              <div data-id="element-1506">
                <h2 className="text-heading-lg font-bold text-surface-900" data-id="element-1507">
                  Transaction Log
                </h2>
                <p className="text-body-sm text-surface-500" data-id="element-1508">
                  Complete audit history
                </p>
              </div>
            </div>
            <span className="px-3 py-1.5 bg-surface-100 text-body-sm font-medium text-surface-600 rounded-lg" data-id="element-1509">
              {filteredLogs.length} records
            </span>
          </div>
          <Table columns={columns} data={filteredLogs} keyExtractor={row => row.id} data-id="element-1510" />
        </Card>
      </motion.div>
    </div>;
}